import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronDown, LogOut } from 'lucide-react';
import {
  NavLink,
  Outlet,
  matchPath,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { Pill, PillGroup } from '@/components/ui';
import { useAuthStore } from '@/stores';
import { Wordmark } from './Wordmark';

interface AdminNavItem {
  label: string;
  to: string;
  /** Match the path exactly; only the dashboard, so it stays inactive on sub-pages. */
  end?: boolean;
}

const ADMIN_NAV: AdminNavItem[] = [
  { label: 'Dashboard', to: '/admin', end: true },
  { label: 'Products', to: '/admin/products' },
  { label: 'Users', to: '/admin/users' },
  { label: 'Designs', to: '/admin/designs' },
  { label: 'Decoration methods', to: '/admin/decoration-methods' },
  { label: 'Onboarding', to: '/admin/onboarding' },
];

/**
 * Frame of the admin area: wordmark, section navigation and the user menu
 * in a 64 px header, the page below via `Outlet`. Mounted as a layout route
 * under `AdminRoute`, so every page inside is already an admin.
 */
export function AdminShell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const handleSignOut = async () => {
    try {
      await logout();
    } finally {
      navigate('/login', { replace: true });
    }
  };

  const userLabel = user?.name || user?.email || 'Account';

  return (
    <div className="flex min-h-screen flex-col bg-paper">
      <header className="flex h-16 items-center gap-6 border-b border-line bg-paper px-6">
        <div className="flex items-center gap-3">
          <Wordmark to="/admin" />
          <span className="dc-eyebrow">Admin</span>
        </div>

        <nav aria-label="Admin" className="flex-1">
          <PillGroup mode="nav">
            {ADMIN_NAV.map((item) => {
              const active =
                matchPath(
                  { path: item.to, end: item.end ?? false },
                  pathname
                ) !== null;
              return (
                <Pill key={item.to} asChild active={active}>
                  <NavLink to={item.to} end={item.end}>
                    {item.label}
                  </NavLink>
                </Pill>
              );
            })}
          </PillGroup>
        </nav>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              className="inline-flex h-10 max-w-[240px] items-center gap-2 rounded-control px-4 font-display text-sm font-semibold text-ink ring-1 ring-inset ring-line transition-[background-color] duration-[120ms] hover:bg-paper-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary data-[state=open]:bg-paper-2"
            >
              <span className="truncate">{userLabel}</span>
              <ChevronDown
                className="h-4 w-4 shrink-0 text-mute"
                aria-hidden="true"
              />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={6}
              className="z-50 min-w-[200px] rounded-card bg-paper-2 p-1 text-ink ring-1 ring-line focus:outline-none"
            >
              {user?.email && (
                <>
                  <DropdownMenu.Label className="truncate px-3 py-2 text-xs text-mute">
                    {user.email}
                  </DropdownMenu.Label>
                  <DropdownMenu.Separator className="my-1 h-px bg-line" />
                </>
              )}
              <DropdownMenu.Item
                onSelect={() => {
                  void handleSignOut();
                }}
                className="flex cursor-pointer select-none items-center gap-2 rounded-control px-3 py-2 text-sm text-ink outline-none data-[highlighted]:bg-paper-3"
              >
                <LogOut className="h-4 w-4 text-mute" aria-hidden="true" />
                Sign out
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </header>

      <main className="flex flex-1 flex-col px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}
